/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentPreview from '../shipments/stops/DocumentPreview';
import { useGetDocuments } from '../../../utils/DocumentsHooks';
import { PROOF_OF_DELIVERY, PROOF_OF_PICKUP } from '../../../config/documents';
import { getSignedUrl } from '../../../utils/DocumentsAPIUtils';
import { FailedDocument } from './FailedDocument';
import { DOCUMENT_STATUSES } from './Document';
import LoadingCircle from '../../ui/LoadingCircle';
import { showToast } from '../../../stores/ToastStore';
import { FETCH_POLICY_NETWORK_ONLY } from '../../../constants/graphql-constants';
export function PodDocumentsList({ shipment, stop, onInit, refetchRef }) {
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const refetchIntervalRef = useRef(0);
    const { t } = useTranslation();
    const { data: documentsData, error: documentError, refetch: documentsRefetch, } = useGetDocuments({
        foreignKeys: [stop ? stop.id : shipment.id].filter((key) => key),
        type: (stop === null || stop === void 0 ? void 0 : stop.action) !== 'deliver'
            ? PROOF_OF_PICKUP.value
            : PROOF_OF_DELIVERY.value,
        fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
    });
    const initDocuments = async () => {
        var _a, _b;
        if (documentError) {
            showToast(t('GET_DOCUMENT_ERROR'), t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: documentError }), 'error');
        }
        if (!documentsData) {
            return;
        }
        setLoading(true);
        const hasPendingDocuments = !!((_a = documentsData === null || documentsData === void 0 ? void 0 : documentsData.documents) === null || _a === void 0 ? void 0 : _a.find((document) => document.status === DOCUMENT_STATUSES.PENDING));
        const hasFailedDocuments = !!((_b = documentsData === null || documentsData === void 0 ? void 0 : documentsData.documents) === null || _b === void 0 ? void 0 : _b.find((document) => document.status === DOCUMENT_STATUSES.UPLOAD_FAILED));
        if (hasPendingDocuments || hasFailedDocuments) {
            clearTimeout(refetchIntervalRef.current);
            refetchIntervalRef.current = setInterval(() => {
                refetchIntervalRef.current = 0;
                documentsRefetch();
            }, 3000);
        }
        const documents = [];
        await Promise.all(documentsData === null || documentsData === void 0 ? void 0 : documentsData.documents.map(async (document) => {
            var _a;
            let signedUrl;
            if (document.status === DOCUMENT_STATUSES.UPLOADED) {
                const { data, errors: signedUrlErrors } = await getSignedUrl(document.key);
                if (signedUrlErrors) {
                    showToast(t('GET_SIGNED_URL_ERROR'), t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
                        error: signedUrlErrors[0].message,
                    }), 'error');
                    return;
                }
                signedUrl = (_a = data === null || data === void 0 ? void 0 : data.getSignedUrl) === null || _a === void 0 ? void 0 : _a.signedUrl;
            }
            documents.push(Object.assign(Object.assign({}, document), { signedUrl }));
        }));
        setDocuments(documents);
        setLoading(false);
        onInit === null || onInit === void 0 ? void 0 : onInit(documents);
    };
    useEffect(() => {
        refetchRef === null || refetchRef === void 0 ? void 0 : refetchRef(documentsRefetch);
        return () => {
            refetchRef === null || refetchRef === void 0 ? void 0 : refetchRef(null);
        };
    }, [documentsRefetch]);
    useEffect(() => {
        initDocuments();
        return () => {
            clearTimeout(refetchIntervalRef.current);
        };
    }, [documentsData, documentError]);
    return (React.createElement("div", { className: "grid-3-x-3" },
        loading && React.createElement(LoadingCircle, { text: t('PROCESSING_FILES') }),
        !loading &&
            documents.map((document, idx) => (React.createElement("div", { key: idx, className: "px-4 py-2 m-auto" }, document.status === DOCUMENT_STATUSES.UPLOAD_FAILED ? (React.createElement(FailedDocument, { document: document, onDelete: () => documentsRefetch() })) : document.status === DOCUMENT_STATUSES.PENDING ? (React.createElement(LoadingCircle, { text: "Loading..." })) : (React.createElement(DocumentPreview, { url: document.signedUrl, displayInGridView: true, renderDownloadableImage: true })))))));
}
